/**
 * SEO configuration and structured-data helpers.
 */

import { getSiteUrl } from './environment';
import { SUPPORT_EMAIL } from './constants';

const SITE_NAME = 'Solidly Meet';
const DEFAULT_TITLE = 'Solidly Meet — Bring Claude into your meetings. Live.';
const DEFAULT_DESCRIPTION =
  'Claude in your meetings, live — no bot joins the call. A local-first desktop app with skills for every phase: pre-meeting skills draft your agenda, in-meeting skills surface support cards as the conversation needs them, and post-meeting skills name speakers by voice recognition and deliver notes to your tools via MCP. Runs on your own Claude. Buy once, own forever.';

export const getDefaultSEO = () => {
  const siteUrl = getSiteUrl();
  return {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    keywords:
      'meeting assistant, live meeting AI, Claude, Claude meeting assistant, meeting transcription, no-bot notetaker, botless meeting notes, pre-meeting agenda, meeting prep AI, voice recognition diarization, speaker identification, MCP, Notion meeting notes, local-first, AssemblyAI, sales call assistant',
    canonical: siteUrl,
    ogTitle: DEFAULT_TITLE,
    ogDescription: DEFAULT_DESCRIPTION,
    ogImage: `${siteUrl}/og-image.jpg`,
    twitterImage: `${siteUrl}/twitter-image.jpg`,
  };
};

export const getOrganizationSchema = () => {
  const siteUrl = getSiteUrl();
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: siteUrl,
    logo: `${siteUrl}/logo512.png`,
  };
};

export const getSoftwareApplicationSchema = () => {
  const siteUrl = getSiteUrl();
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: SITE_NAME,
    applicationCategory: 'BusinessApplication',
    // Honest platform claim: the shipping installer is Windows; macOS is coming.
    operatingSystem: 'Windows',
    description: DEFAULT_DESCRIPTION,
    url: siteUrl,
    offers: {
      '@type': 'Offer',
      price: '25',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    },
  };
};

// Keep this list in sync with the on-page FAQ in components/Faq.js.
export const getFAQSchema = () => {
  const qa = [
    {
      q: 'What do I need to run it?',
      a: 'Three things: a Windows PC (the macOS build is on the way), Claude Code installed with a Claude subscription (Pro or Max — the app runs on your plan), and an AssemblyAI API key for live transcription (they have a free tier). MCP servers are optional — you only need them if you want notes delivered to tools like Notion or Slack.',
    },
    {
      q: 'Are there ongoing costs or subscription fees?',
      a: 'No subscription of any kind — the app is a one-time purchase you own forever, including all future updates. For transcription you have two no-subscription options: bring your own AssemblyAI key and pay AssemblyAI directly for exactly what you use, or use the transcription credits included with your purchase and top up only if you need more. Claude always runs on your own plan.',
    },
    {
      q: 'Is my meeting data private?',
      a: "The app is local-first: transcripts, notes, and voice recognition data live in a SQLite database on your device, there's no cloud account, and we run no servers that touch your meeting content. Exactly two things leave your machine, both under your own keys: meeting audio goes to AssemblyAI for transcription on your API key, and prompts go to Claude on your subscription. We never see either.",
    },
    {
      q: 'How do live support cards work?',
      a: 'You teach the app "meeting skills" — Claude playbooks with a "fires when…" hint, created by chatting with the built-in skills assistant. During a meeting, your in-meeting skills watch the live transcript and surface compact markdown cards the moment they\'re relevant: pricing talking points when pricing comes up, an objection counter when the customer pushes back, anything you\'ve taught it.',
    },
    {
      q: 'What are pre-meeting and post-meeting skills?',
      a: 'Skills come in three kinds, one per phase of the meeting — and you create all three the same way, by chatting with the built-in skills assistant. Pre-meeting skills run on a schedule against your calendar (through an MCP server you connect), pull the day\'s meetings into the app, and draft an agenda for each. In-meeting skills surface live support cards during the call. Post-meeting skills deliver the finished notes to your tools via MCP. The app knows which phase each skill belongs to and fires it at the right moment.',
    },
    {
      q: 'Can it join my meeting as a bot?',
      a: "No — and that's by design, not a missing feature. The app captures your mic and system audio locally on your machine, so nothing dials into the call, nobody has to admit a notetaker, and the other side of the call never deals with a bot. It works with any meeting app that plays audio through your computer.",
    },
    {
      q: 'How does speaker identification work without a bot?',
      a: 'Real voice recognition diarization, after the meeting: the app learns voices across your meetings, proposes who said what, and you confirm the names — including adjusting the speaker count if it guessed wrong. Once it knows a voice, the name sticks in future meetings.',
    },
    {
      q: 'How do the handoffs to Claude work?',
      a: 'After the meeting, Claude drafts next steps. Each one has a button that opens claude.ai with a fully-briefed prompt — the context, the decision, who asked. The conversation continues in your real Claude history on your own account, not inside our app.',
    },
    {
      q: 'Which platforms are supported?',
      a: "The desktop app runs on Windows today; the macOS build is coming. Because capture is local audio, it works alongside any meeting software — Zoom, Meet, Teams, or a phone call playing through your machine.",
    },
    {
      q: 'How many devices can I use it on?',
      a: `Your license activates on up to 3 devices — desktop and laptop both, no extra charge. Moving to a new machine? Email ${SUPPORT_EMAIL} and we'll free up a slot.`,
    },
    {
      q: 'What is your refund policy?',
      a: `A 7-day money-back guarantee. If it is not for you, email ${SUPPORT_EMAIL} within 7 days of purchase for a full refund — no forms, no questions.`,
    },
    {
      q: 'Do I get updates?',
      a: 'Yes — every future update is included free, for the lifetime of the product. The app updates itself automatically, with no upgrade fees and no subscription required.',
    },
    {
      q: 'How do I get help?',
      a: `Email ${SUPPORT_EMAIL} — questions, bug reports, and refunds all go to the same address, and a human reads every message.`,
    },
  ];

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: qa.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };
};
